import { Pressable, Text, View, type ViewStyle } from "react-native";
import { Check } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { getInteractionDelta } from "../utils/interactionStyles";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Square checkbox with optional inline label. Audit task 2.5.
 *
 * Replaces the lovable Switch-style stand-in (`packages/lovable/checkbox.rn`)
 * which renders an empty inner View instead of a check glyph. This shape
 * shows the canonical `Check` icon when checked and uses warm-tinted fill
 * to match the rest of the moon palette.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / checked  — fill + icon swap
 *   - disabled        — opacity 0.5, presses ignored
 *   - error           — coral border when `error` is true (rare for checkbox)
 *   - focused / loading — N/A. Wrap inside MoonFormField for label/error
 *     chrome on a group of checkboxes.
 */
type Props = {
  value: boolean;
  onChange: (next: boolean) => void;
  label?: string;
  /** Hue used for the checked fill. Defaults to warm. */
  accentColor?: string;
  /** Color of the check glyph when checked. Defaults to white. */
  iconColor?: string;
  /** Background color of the unchecked box. Defaults to a translucent surface tint. */
  uncheckedBackgroundColor?: string;
  /**
   * Place the label on the left and the box on the right (setting-row layout).
   * Defaults to false (box on the left, label on the right).
   */
  reverse?: boolean;
  disabled?: boolean;
  error?: boolean;
  /**
   * Stable analytics identifier. The new value is never captured — a
   * checkbox can record a clinical fact.
   */
  action?: string;
  testID?: string;
  style?: ViewStyle;
};

const BOX_SIZE = 20;
const ICON_SIZE = 14;

export function MoonCheckbox({
  value,
  onChange,
  label,
  accentColor = colors.warm,
  iconColor = "#FFFFFF",
  uncheckedBackgroundColor = "rgba(255, 255, 255, 0.5)",
  reverse = false,
  disabled,
  error,
  action,
  testID,
  style,
}: Props) {
  const capturePress = usePressCapture("MoonCheckbox");
  const box = (
    <View
      style={{
        width: BOX_SIZE,
        height: BOX_SIZE,
        borderRadius: 6,
        borderWidth: 1.5,
        borderColor: error ? colors.coral : value ? accentColor : colors.border,
        backgroundColor: value ? accentColor : uncheckedBackgroundColor,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {value ? (
        <Check size={ICON_SIZE} color={iconColor} strokeWidth={3} />
      ) : null}
    </View>
  );

  const text = label ? (
    <Text
      style={{
        fontFamily: typography.families.sans,
        fontSize: 14,
        color: colors.text,
        flex: reverse ? 1 : undefined,
        flexShrink: reverse ? undefined : 1,
        marginLeft: reverse ? 0 : 10,
        marginRight: reverse ? 10 : 0,
      }}
    >
      {label}
    </Text>
  ) : null;

  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: value, disabled }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.checked to the DOM, so mirror the checked state
      // via the aria-checked prop (RN forwards it, RNW renders it as the
      // aria-checked attribute) so screen readers announce it and E2E
      // assertions can read it. Purely observational — does not affect behavior.
      aria-checked={value}
      onPress={() => {
        capturePress(pressLabel(label), action);
        onChange(!value);
      }}
      disabled={disabled}
      testID={testID}
    >
      {({ hovered, focused }) => (
        <View
          style={[
            {
              flexDirection: "row",
              alignItems: "center",
              opacity: disabled ? 0.5 : 1,
            },
            style,
            getInteractionDelta({ hovered, focused }),
          ]}
        >
          {reverse ? (
            <>
              {text}
              {box}
            </>
          ) : (
            <>
              {box}
              {text}
            </>
          )}
        </View>
      )}
    </Pressable>
  );
}
